import { useEffect, useRef } from 'react';
import { afterPageLoad } from '../lib/afterPageLoad';
import { TAU, rand, soften, makeHead, makeLeaf, makeBloom, makeGlow, GRAIN } from './meadow/sprites';

// Firefly Meadow: dark mode home background (v2).
// A dreamy night over a sunflower field. A dusty, star-filled sky, sunflowers
// with depth of field (far ones soft, near ones out of focus), warm bokeh and
// floating pollen. Fireflies drift and meet in pairs. One warm firefly (the
// Zuzu companion) stays near the visitor, and the sunflowers turn toward it.

type Star = { x: number; y: number; r: number; peak: number; period: number; phase: number; col: string };
type Leaf = { t: number; side: 1 | -1; size: number; droop: number };
type Flower = {
  x: number; h: number; r: number; layer: number; phase: number; tilt: number;
  yaw: number; turn: number; head: HTMLCanvasElement; leaves: Leaf[]; halo: number;
};
type Bloom = { x: number; y: number; r: number; phase: number; sprite: HTMLCanvasElement; stem: number };
type Fly = { x: number; y: number; vx: number; vy: number; phase: number; speed: number; partner: Fly | null; meetT: number };
type Mote = { x: number; y: number; vy: number; r: number; phase: number };
type Orb = { x: number; y: number; r: number; a: number; phase: number; warm: boolean };

export default function FireflyMeadow({ className = '' }: { className?: string }) {
  const backRef = useRef<HTMLCanvasElement>(null);
  const frontRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = backRef.current;
    const fcanvas = frontRef.current;
    if (!canvas || !fcanvas) return;
    const ctx = canvas.getContext('2d')!;
    const fctx = fcanvas.getContext('2d')!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Sprites. Far layers are tinted and softened, the focus layer is sharp.
    const heads = [
      Array.from({ length: 3 }, () => soften(makeHead(0.55), 5)),
      Array.from({ length: 3 }, () => soften(makeHead(0.3), 2.5)),
      Array.from({ length: 4 }, () => makeHead(0.05)),
      Array.from({ length: 2 }, () => soften(makeHead(0.15), 2)),
    ];
    const leaves = [soften(makeLeaf(0.55), 4), soften(makeLeaf(0.3), 2), makeLeaf(0.08), makeLeaf(0.2)];
    const blooms = [
      makeBloom('#ffb238', 8), makeBloom('#ff8a2a', 6), makeBloom('#ffd45a', 10), makeBloom('#f2742a', 7),
    ];
    const flyGlow = makeGlow('225, 255, 150');
    const warmGlow = makeGlow('255, 196, 100');
    const tealGlow = makeGlow('150, 210, 230', 0.3);
    const starGlow = makeGlow('220, 235, 255');
    const sky = document.createElement('canvas');

    let W = 0, H = 0, dpr = 1, t = 0, raf = 0;
    let stars: Star[] = [], flowers: Flower[] = [], fgFlowers: Flower[] = [];
    let wild: Bloom[] = [], fgWild: Bloom[] = [], flies: Fly[] = [], motes: Mote[] = [], orbs: Orb[] = [];
    let shoot: { x: number; y: number; vx: number; vy: number; life: number } | null = null;
    const buddy = { x: 0, y: 0 };
    const pointer = { x: 0, y: 0, last: -1e9 };

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      W = canvas.clientWidth || window.innerWidth;
      H = canvas.clientHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      // The front layer is blurred with CSS, so 1x resolution is plenty.
      fcanvas.width = W;
      fcanvas.height = H;
      const mobile = W < 768;
      const k = mobile ? 0.55 : 1;

      // Static sky: teal night, a band of star dust, and warm light from the field.
      sky.width = canvas.width;
      sky.height = canvas.height;
      const s = sky.getContext('2d')!;
      s.setTransform(dpr, 0, 0, dpr, 0, 0);
      const g = s.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, '#05162b');
      g.addColorStop(0.45, '#0a2a47');
      g.addColorStop(0.75, '#113856');
      g.addColorStop(1, '#2b3a4a');
      s.fillStyle = g;
      s.fillRect(0, 0, W, H);
      const haze = (cx: number, cy: number, r: number, col: string) => {
        const n = s.createRadialGradient(cx, cy, 0, cx, cy, r);
        n.addColorStop(0, col);
        n.addColorStop(1, 'rgba(0,0,0,0)');
        s.fillStyle = n;
        s.fillRect(0, 0, W, H);
      };
      // Milky way: soft clouds and dense dust along a diagonal.
      const band = (u: number) => ({ x: W * (-0.1 + u * 1.2), y: H * (0.62 - u * 0.55) });
      for (let i = 0; i < 14; i++) {
        const p = band(i / 13);
        haze(p.x, p.y + rand(-30, 30), rand(0.12, 0.22) * W, `rgba(${rand(90, 130) | 0}, ${rand(150, 190) | 0}, 210, 0.07)`);
      }
      const dust = mobile ? 1400 : 3200;
      for (let i = 0; i < dust; i++) {
        const inBand = Math.random() < 0.55;
        let px: number, py: number;
        if (inBand) {
          const p = band(Math.random());
          const spread = (Math.random() + Math.random() + Math.random() - 1.5) * H * 0.16;
          px = p.x + spread * 0.5;
          py = p.y + spread;
        } else {
          px = Math.random() * W;
          py = Math.random() * H * 0.85;
        }
        s.fillStyle = `rgba(${200 + Math.random() * 55}, ${215 + Math.random() * 40}, 255, ${rand(0.08, 0.5)})`;
        s.fillRect(px, py, rand(0.4, 1.1), rand(0.4, 1.1));
      }
      haze(W * 0.5, H * 1.08, W * 0.75, 'rgba(255, 165, 70, 0.32)');
      haze(W * 0.06, H * 1.0, W * 0.32, 'rgba(255, 175, 80, 0.26)');
      haze(W * 0.94, H * 1.0, W * 0.32, 'rgba(255, 175, 80, 0.26)');

      const starCols = ['235, 242, 255', '200, 225, 255', '255, 240, 215', '180, 215, 255'];
      // Same gentle twinkle as the Sky of Thoughts: each star fades between
      // 0.2 and its own peak, and grows a little, over 2 to 5 seconds.
      stars = Array.from({ length: Math.round(160 * k) }, () => ({
        x: Math.random() * W,
        y: Math.pow(Math.random(), 1.3) * H * 0.82,
        r: rand(0.25, 1.25),
        peak: rand(0.3, 1),
        period: rand(2, 5),
        phase: Math.random() * TAU,
        col: starCols[Math.random() * starCols.length | 0],
      }));

      // Sunflowers. Layer 0 and 1 are far and soft, layer 2 is in focus on
      // the sides (where the book stacks were), layer 3 is huge and blurred
      // in the corners on the front canvas.
      const mk = (layer: number, n: number, pickX: () => number, r: [number, number], h: [number, number]): Flower[] =>
        Array.from({ length: n }, () => {
          const rr = rand(...r) * (mobile ? 0.72 : 1);
          const set = heads[layer];
          return {
            x: pickX(), r: rr, h: rand(...h) * H, layer,
            phase: Math.random() * TAU, tilt: rand(-0.3, 0.3),
            yaw: rand(-0.55, 0.55), turn: 0,
            head: set[Math.random() * set.length | 0],
            halo: rand(0.08, 0.2),
            leaves: Array.from({ length: 2 + (Math.random() * 3 | 0) }, (_, i) => ({
              t: rand(0.2, 0.7), side: (i % 2 ? 1 : -1) as 1 | -1, size: rr * rand(1.1, 1.7), droop: rand(0.2, 0.7),
            })),
          };
        });
      const any = () => rand(-0.03, 1.03) * W;
      const sides = () => (Math.random() < 0.5 ? rand(-0.03, 0.2) : rand(0.8, 1.03)) * W;
      const corners = () => (Math.random() < 0.5 ? rand(-0.08, 0.1) : rand(0.9, 1.08)) * W;
      flowers = [
        ...mk(0, Math.round(46 * k), any, [7, 13], [0.08, 0.17]),
        ...mk(1, Math.round(24 * k), any, [15, 24], [0.12, 0.22]),
        ...mk(2, Math.round(9 * k) || 4, sides, [36, 56], [0.24, 0.44]),
      ].sort((a, b) => a.layer - b.layer || b.h - a.h);
      fgFlowers = mk(3, mobile ? 2 : 4, corners, [90, 140], [0.1, 0.24]);

      const mkWild = (n: number, y: [number, number], r: [number, number]): Bloom[] =>
        Array.from({ length: n }, () => ({
          x: any(), y: rand(...y) * H, r: rand(...r) * (mobile ? 0.8 : 1), phase: Math.random() * TAU,
          sprite: blooms[Math.random() * blooms.length | 0], stem: rand(8, 30),
        })).sort((a, b) => a.y - b.y);
      wild = mkWild(Math.round(170 * k), [0.84, 1.0], [3, 8]);
      fgWild = mkWild(Math.round(26 * k), [0.9, 1.04], [14, 26]);

      flies = Array.from({ length: Math.round(40 * k) }, () => ({
        x: Math.random() * W, y: rand(0.35, 0.95) * H, vx: 0, vy: 0,
        phase: Math.random() * TAU, speed: rand(0.8, 1.6), partner: null, meetT: 0,
      }));
      motes = Array.from({ length: Math.round(70 * k) }, () => ({
        x: Math.random() * W, y: rand(0.3, 1) * H, vy: rand(0.05, 0.22), r: rand(0.6, 1.6), phase: Math.random() * TAU,
      }));
      orbs = Array.from({ length: Math.round(14 * k) }, () => ({
        x: Math.random() * W, y: rand(0.72, 1.02) * H, r: rand(30, 90), a: rand(0.1, 0.25),
        phase: Math.random() * TAU, warm: Math.random() < 0.8,
      }));
      buddy.x = W * 0.5;
      buddy.y = H * 0.72;
    };

    const drawFlower = (c: CanvasRenderingContext2D, f: Flower, px: number, wind: number, active: boolean) => {
      const par = [4, 8, 16, 30][f.layer];
      const baseX = f.x - px * par;
      const sway = (Math.sin(t * 0.7 + f.phase + f.x * 0.004) * 0.05 + wind * 0.04) * (1.3 - f.layer * 0.25);
      const target = active ? Math.max(-1, Math.min(1, (pointer.x - baseX) / (W * 0.4))) : 0;
      f.turn += (target - f.turn) * 0.02;
      const topX = baseX + Math.sin(sway) * f.h + f.turn * f.r * 0.4;
      const topY = H - f.h * Math.cos(sway);
      const ctrlX = baseX + Math.sin(sway) * f.h * 0.3;

      // Stem with a lighter side.
      const stemW = Math.max(1.4, f.r * 0.13);
      c.lineCap = 'round';
      c.strokeStyle = ['#15301f', '#1d3d22', '#2a5227', '#1e3a1c'][f.layer];
      c.lineWidth = stemW;
      c.beginPath();
      c.moveTo(baseX, H + 6);
      c.quadraticCurveTo(ctrlX, H - f.h * 0.55, topX, topY + f.r * 0.2);
      c.stroke();
      if (f.layer >= 2) {
        c.strokeStyle = 'rgba(140, 190, 100, 0.35)';
        c.lineWidth = stemW * 0.3;
        c.beginPath();
        c.moveTo(baseX - stemW * 0.25, H + 6);
        c.quadraticCurveTo(ctrlX - stemW * 0.25, H - f.h * 0.55, topX - stemW * 0.25, topY + f.r * 0.2);
        c.stroke();
      }

      const leaf = leaves[f.layer];
      for (const l of f.leaves) {
        const u = l.t;
        const lx = (1 - u) * (1 - u) * baseX + 2 * (1 - u) * u * ctrlX + u * u * topX;
        const ly = H - f.h * u;
        c.save();
        c.translate(lx, ly);
        c.rotate((l.side > 0 ? -0.2 + l.droop : Math.PI + 0.2 - l.droop) + sway + Math.sin(t * 1.1 + f.phase + u * 5) * 0.04);
        if (l.side < 0) c.scale(1, -1);
        c.drawImage(leaf, 0, -l.size * 0.36, l.size, l.size * 0.72);
        c.restore();
      }

      // Warm halo behind the focus flowers, so they feel lit from within.
      const size = f.r * 2.8;
      if (f.layer === 2) {
        c.globalCompositeOperation = 'lighter';
        c.globalAlpha = f.halo;
        c.drawImage(warmGlow, topX - size, topY - size, size * 2, size * 2);
        c.globalAlpha = 1;
        c.globalCompositeOperation = 'source-over';
      }

      c.save();
      c.translate(topX, topY);
      c.rotate(sway * 0.8 + f.tilt * 0.4 + f.turn * 0.12);
      c.scale(Math.max(0.35, Math.cos(f.yaw + f.turn * 0.5)), 0.9);
      c.drawImage(f.head, -size / 2, -size / 2, size, size);
      c.restore();
    };

    const drawWild = (c: CanvasRenderingContext2D, list: Bloom[], px: number, par: number) => {
      c.strokeStyle = 'rgba(30, 60, 30, 0.9)';
      c.lineWidth = 1;
      for (const b of list) {
        const sw = Math.sin(t * 0.9 + b.phase + b.x * 0.01) * b.r * 0.35;
        const bx = b.x - px * par + sw;
        c.beginPath();
        c.moveTo(b.x - px * par, b.y + b.stem);
        c.lineTo(bx, b.y);
        c.stroke();
        const s = b.r * 4;
        c.drawImage(b.sprite, bx - s / 2, b.y - s / 2, s, s);
      }
    };

    const frame = () => {
      if (!sky.width || !sky.height) return;
      t += 1 / 60;
      const active = t - pointer.last < 4;
      const px = active ? pointer.x / W - 0.5 : 0;
      const wind = Math.sin(t * 0.3) * 0.5 + Math.sin(t * 0.83) * 0.2;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(sky, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Twinkling stars with a soft glow.
      ctx.globalCompositeOperation = 'lighter';
      for (const s of stars) {
        const w = 0.5 - 0.5 * Math.cos((t / s.period) * TAU + s.phase);
        const a = 0.2 + (s.peak - 0.2) * w;
        const r = s.r * (0.8 + 0.4 * w);
        const sx = s.x - px * 5;
        ctx.globalAlpha = a * 0.5;
        ctx.drawImage(starGlow, sx - r * 3, s.y - r * 3, r * 6, r * 6);
        ctx.globalAlpha = 1;
        ctx.fillStyle = `rgba(${s.col}, ${a})`;
        ctx.beginPath();
        ctx.arc(sx, s.y, r, 0, TAU);
        ctx.fill();
      }

      if (!shoot && Math.random() < 0.0012) {
        shoot = { x: rand(0.1, 0.7) * W, y: rand(0.03, 0.3) * H, vx: rand(7, 10), vy: rand(2, 3.5), life: 1 };
      }
      if (shoot) {
        shoot.x += shoot.vx; shoot.y += shoot.vy; shoot.life -= 0.02;
        const g = ctx.createLinearGradient(shoot.x, shoot.y, shoot.x - shoot.vx * 14, shoot.y - shoot.vy * 14);
        g.addColorStop(0, `rgba(255,255,255,${Math.max(0, shoot.life)})`);
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(shoot.x, shoot.y);
        ctx.lineTo(shoot.x - shoot.vx * 14, shoot.y - shoot.vy * 14);
        ctx.stroke();
        if (shoot.life <= 0) shoot = null;
      }
      ctx.globalCompositeOperation = 'source-over';

      // Field: far sunflowers, small glowing wildflowers, then the focus sunflowers.
      let i = 0;
      for (; i < flowers.length && flowers[i].layer < 2; i++) drawFlower(ctx, flowers[i], px, wind, active);
      drawWild(ctx, wild, px, 10);
      for (; i < flowers.length; i++) drawFlower(ctx, flowers[i], px, wind, active);

      // Low warm glow over the field.
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.18 + 0.04 * Math.sin(t * 0.4);
      ctx.drawImage(warmGlow, -W * 0.2, H * 0.72, W * 1.4, H * 0.6);
      ctx.globalAlpha = 1;

      // Pollen drifting up.
      for (const m of motes) {
        m.y -= m.vy;
        m.x += Math.sin(t * 0.5 + m.phase) * 0.15;
        if (m.y < H * 0.25) { m.y = H + 5; m.x = Math.random() * W; }
        const a = 0.25 + 0.35 * (0.5 + 0.5 * Math.sin(t * 1.5 + m.phase));
        ctx.fillStyle = `rgba(255, 220, 150, ${a})`;
        ctx.beginPath();
        ctx.arc(m.x - px * 12, m.y, m.r, 0, TAU);
        ctx.fill();
      }

      // Fireflies: blink, drift, gather gently toward the visitor, and pair up.
      if (Math.random() < 0.006) {
        const a = flies[Math.random() * flies.length | 0];
        const b = flies[Math.random() * flies.length | 0];
        if (a !== b && !a.partner && !b.partner) { a.partner = b; b.partner = a; a.meetT = b.meetT = 0; }
      }
      for (const f of flies) {
        f.vx += (Math.random() - 0.5) * 0.06;
        f.vy += (Math.random() - 0.5) * 0.06;
        if (active) {
          const dx = pointer.x - f.x, dy = pointer.y - f.y;
          const d = Math.hypot(dx, dy);
          if (d < 320 && d > 40) { f.vx += dx / d * 0.012; f.vy += dy / d * 0.012; }
        }
        let bright = 1;
        if (f.partner) {
          const dx = f.partner.x - f.x, dy = f.partner.y - f.y;
          const d = Math.hypot(dx, dy);
          if (d > 16) { f.vx += dx / d * 0.03; f.vy += dy / d * 0.03; }
          else { f.meetT += 1 / 60; bright = 1.8; }
          if (f.meetT > 3) { f.partner.partner = null; f.partner = null; }
        }
        f.vx *= 0.97; f.vy *= 0.97;
        f.x += f.vx; f.y += f.vy;
        if (f.x < -20) f.x = W + 20;
        if (f.x > W + 20) f.x = -20;
        if (f.y < H * 0.3) f.vy += 0.03;
        if (f.y > H) f.vy -= 0.05;

        const blink = Math.pow(Math.max(0, Math.sin(t * f.speed + f.phase)), 2);
        const a = Math.min(1, (0.15 + 0.85 * blink) * bright);
        const r = 10 + 12 * a * bright;
        ctx.globalAlpha = a * 0.85;
        ctx.drawImage(flyGlow, f.x - r, f.y - r, r * 2, r * 2);
        ctx.globalAlpha = 1;
        ctx.fillStyle = `rgba(250, 255, 215, ${0.3 + 0.7 * a})`;
        ctx.beginPath();
        ctx.arc(f.x, f.y, 1.3, 0, TAU);
        ctx.fill();
      }

      // The Zuzu companion firefly.
      const tx = active ? pointer.x + 26 : W * 0.5 + Math.sin(t * 0.3) * W * 0.18;
      const ty = active ? pointer.y - 20 : H * 0.72 + Math.cos(t * 0.45) * H * 0.04;
      buddy.x += (tx - buddy.x) * 0.035;
      buddy.y += (ty - buddy.y) * 0.035;
      const br = 36 * (0.75 + 0.25 * Math.sin(t * 2));
      ctx.drawImage(warmGlow, buddy.x - br, buddy.y - br, br * 2, br * 2);
      ctx.fillStyle = '#fff4cf';
      ctx.beginPath();
      ctx.arc(buddy.x, buddy.y, 2.4, 0, TAU);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';

      // Front layer (blurred by CSS): out-of-focus blooms and bokeh.
      fctx.clearRect(0, 0, W, H);
      drawWild(fctx, fgWild, px, 40);
      for (const f of fgFlowers) drawFlower(fctx, f, px, wind, active);
      fctx.globalCompositeOperation = 'lighter';
      for (const o of orbs) {
        o.x += Math.sin(t * 0.2 + o.phase) * 0.12;
        const a = o.a * (0.6 + 0.4 * Math.sin(t * 0.5 + o.phase));
        fctx.globalAlpha = a;
        fctx.drawImage(o.warm ? warmGlow : tealGlow, o.x - o.r - px * 40, o.y - o.r, o.r * 2, o.r * 2);
      }
      fctx.globalAlpha = 1;
      fctx.globalCompositeOperation = 'source-over';
    };

    const loop = () => {
      frame();
      raf = requestAnimationFrame(loop);
    };
    const start = () => { if (!raf && !reduced) raf = requestAnimationFrame(loop); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };

    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.last = t;
    };
    const onVis = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => { build(); frame(); }, 150);
    };

    // The first still frame draws right away; the movement starts once the page has loaded.
    build();
    frame();
    const cancelStart = afterPageLoad(start);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerdown', onMove, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelStart();
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onMove);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div aria-hidden="true" className={`overflow-hidden pointer-events-none ${className}`}>
      <canvas ref={backRef} className="absolute inset-0 block w-full h-full" />
      <canvas ref={frontRef} className="absolute inset-0 block w-full h-full" style={{ filter: 'blur(7px)' }} />
      {/* Dreamy finish: soft vignette and a touch of film grain. */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 45%, transparent 55%, rgba(2, 10, 22, 0.55) 100%)' }}
      />
      <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{ backgroundImage: GRAIN }} />
    </div>
  );
}
